let _Math = {
  round(n, factor) {
    if (factor === 0) {
      return Math.round(n * (10 ** 10)) / (10 ** 10);
    }

    let f = factor || (10 ** Math.floor(Math.log(Math.abs(n)) * Math.LOG10E)) || 1;

    return (n > 0 ? Math.ceil(n / f) : Math.floor(n / f)) / (1 / f);
  },

  rand(min, max) {
    return min + (Math.random() * (max - min));
  },

  filter(arr, accessor) {
    return arr.map(function (a) {
      if (a instanceof Object) {
        return accessor.call ? accessor(a) : a[accessor];
      }

      return a;
    }).filter(function (a) {
      return a !== undefined && a !== null && !isNaN(a);
    });
  },

  sum(arr, accessor) {
    let vals = this.filter(arr, accessor);

    return vals.reduce((a, b) => a + (+b), 0);
  },

  avg(arr, accessor) {
    let vals = this.filter(arr, accessor);

    return vals.reduce((a, b) => a + (+b), 0) / vals.length;
  },

  median(arr, accessor) {
    let vals = this.filter(arr, accessor).sort((a, b) => (a > b ? 1 : -1));

    let n = vals.length;

    if (n === 0) {
      return NaN;
    }

    if (n === 1) {
      return +vals[0];
    }

    if (n % 2 === 1) {
      return +vals[Math.floor(n / 2)];
    }

    return (+vals[n / 2] + +vals[(n / 2) - 1]) / 2;
  },

  min(arr, accessor) {
    let vals = this.filter(arr, accessor);

    return vals.length ? Math.min.apply(null, vals) : NaN;
  },

  max(arr, accessor) {
    let vals = this.filter(arr, accessor);

    return vals.length ? Math.max.apply(null, vals) : NaN;
  },

  // http://mathworld.wolfram.com/SampleVariance.html
  variance(arr, accessor) {
    if (arr.length < 2) {
      return NaN;
    }

    let vals = this.filter(arr, accessor);
    let avg = this.avg(vals);
    let sum = this.sum(vals.map(function (val) {
      return (val - avg) ** 2;
    }));

    return sum / (vals.length - 1);
  },

  deviation(arr, accessor) {
    return Math.sqrt(this.variance(arr, accessor));
  },

  // http://onlinestatbook.com/2/regression/intro.html
  // http://onlinestatbook.com/2/describing_bivariate_data/calculation.html
  trend(points) {
    let x = points.map((p) => p[0]);
    let y = points.map((p) => p[1]);
    let xMean = this.avg(x);
    let yMean = this.avg(y);
    let xDev = this.deviation(x);
    let yDev = this.deviation(y);
    let xVar = x.map((x) => x - xMean);
    let yVar = y.map((y) => y - yMean);
    let zip = xVar.map(function (a, i) {
      let b = yVar[i];

      return {
        xy: a * b,
        x2: a ** 2,
        y2: b ** 2
      };
    });
    let xySum = this.sum(zip.map((z) => z.xy));
    let x2Sum = this.sum(zip.map((z) => z.x2));
    let y2Sum = this.sum(zip.map((z) => z.y2));

    let r = xySum / Math.sqrt(x2Sum * y2Sum);
    let b = (r * (yDev / xDev)) || 0;
    let A = (yMean - (b * xMean)) || 0;

    return function (x) {
      return (b * x) + A;
    };
  },

  // https://gist.github.com/matt-west/6500993#gistcomment-2743187
  correlation(arr1, arr2) {
    let add = (a, b) => a + b;

    let n = Math.min(arr1.length, arr2.length);

    if (n === 0) {
      return 0;
    }

    let [d1, d2] = [arr1.slice(0, n), arr2.slice(0, n)];

    let [sum1, sum2] = [d1, d2].map((l) => l.reduce(add));

    let [pow1, pow2] = [d1, d2].map((l) => l.reduce((a, b) => a + (b ** 2), 0));

    let mulSum = d1.map((n, i) => n * d2[i]).reduce(add);

    let dense = Math.sqrt((pow1 - ((sum1 ** 2) / n)) * (pow2 - ((sum2 ** 2) / n)));

    if (dense === 0) {
      return 0;
    }

    return (mulSum - (sum1 * sum2 / n)) / dense;
  },

  nearest(arr, val, threshold, accessor) {
    let t = threshold || Infinity;
    let a = accessor || ((v) => v);
    let diffs = arr.map((v) => Math.abs(a(v) - val));
    let min = Math.min.apply(null, diffs);

    if (min <= Math.abs(t)) {
      return arr[diffs.indexOf(min)];
    }

    return undefined;
  },

  limit(n, min, max) {
    return Math.max(min, Math.min(n, max));
  },

  ticks(yMin, yMax, steps, maxTicks) {
    let factors = [].concat(steps).sort((a, b) => a - b);
    let a;
    let b;
    let len;

    if (yMin === yMax) {
      return [this.round(yMin - 0.00001, -steps[0]), this.round(yMax + 0.00001, +steps[0])];
    }

    for (let i = 0, f = 1; i < factors.length, f = factors[i]; i++) {
      a = this.round(yMin, yMin > 0 ? -f : f);
      b = this.round(yMax, yMax > 0 ? f : -f);
      len = Math.round((b - a) / f);

      if (len < maxTicks) {
        return Array(len + 1).fill(0).map(function (n, i) {
          return a + (i * f);
        });
      }
    }

    return [a, b];
  },

  duration(millis) {
    let hours = Math.floor(millis / 3600000);
    let minutes = Math.floor((millis / 60000) % 60);
    let seconds = Math.floor((millis / 1000) % 60);

    return { hours, minutes, seconds };
  },

  overlaps(a, b) {
    return Math.max(a[0], b[0]) <= Math.min(a[1], b[1]);
  },

  POUNDS_PER_KILO: 2.20462,

  KILOS_PER_POUND: 0.453592,

  METERS_PER_INCH: 0.0254,

  INCHES_PER_METER: 39.3701,

  MILLIS_PER_MINUTE: 60000,

  MILLIS_PER_HOUR: 3600000,

  MILLIS_PER_DAY: 86400000
};

module.exports = _Math;
